import { useTheme } from "@/contexts/ThemeContext";
import { QrCode, Sun, Moon } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

export default function PrivacyPage() {
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-background" data-testid="privacy-page">
      <SEO
        title="Privacy Policy"
        description="How Smart-QR collects, uses, and protects your data—including profiles, QR analytics, cookies, and third parties like Stripe and Resend."
        canonicalUrl="https://smart-qr.app/privacy"
        ogUrl="https://smart-qr.app/privacy"
        ogImage="https://smart-qr.app/icon-192.png"
      />
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/40">
        <div className="flex items-center justify-between px-6 py-4 max-w-4xl mx-auto">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center">
              <QrCode className="text-primary-foreground" size={16} />
            </div>
            <span className="font-heading text-base font-bold">Smart-QR</span>
          </Link>
          <button onClick={toggleTheme} className="p-2.5 rounded-xl hover:bg-secondary text-muted-foreground">
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </nav>
      <div className="max-w-3xl mx-auto px-6 py-16 animate-fade-in">
        <h1 className="font-heading text-3xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground text-sm mb-10">Last updated: August 2026</p>

        <div className="prose prose-sm max-w-none text-muted-foreground space-y-6 leading-relaxed">
          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground mb-2">1. Information We Collect</h2>
            <p>When you create an account, we collect your email address and profile information you choose to provide (name, bio, links, images). When visitors scan your QR codes, we collect anonymous analytics data such as device type, browser, and timestamp.</p>
          </section>
          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground mb-2">2. How We Use Your Information</h2>
            <p>We use your information to provide the Smart-QR service, including generating QR codes, hosting your public profile pages, and displaying analytics. We do not sell your personal data to third parties.</p>
          </section>
          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground mb-2">3. Data Storage & Security</h2>
            <p>Your data is stored securely and encrypted in transit. We implement industry-standard security measures to protect your information. Uploaded images and files are stored on secure servers.</p>
          </section>
          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground mb-2">4. Cookies & Tracking</h2>
            <p>We use essential cookies for authentication and session management. With your permission, we also use Google Ads measurement and PostHog analytics to understand visits and whether advertising leads to useful actions. Optional analytics and advertising storage is denied until you make a choice in our consent banner, and you may reject it without affecting essential account features. Analytics data collected from QR scans is anonymized and used to provide scan insights.</p>
          </section>
          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground mb-2">5. Third-Party Services</h2>
            <p>We use Stripe for payment processing, Resend for transactional emails such as password resets, Google Ads for consent-controlled advertising measurement, and PostHog for consent-controlled product analytics. Each provider processes data under its own privacy terms.</p>
          </section>
          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground mb-2">6. Your Rights</h2>
            <p>You may access, update, or delete your account and data at any time through your dashboard settings. To request data deletion, contact us at support@smartqr.app.</p>
          </section>
          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground mb-2">7. Contact</h2>
            <p>If you have questions about this privacy policy, please contact us at support@smartqr.app.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
